import { GarmentType } from '@/types/orders'

export type GarmentSubGarmentOption = {
  value: string
  label: string
}

export const GARMENT_TYPE_OPTIONS: Array<{ value: GarmentType; label: string }> = [
  { value: GarmentType.JACKET_VEST, label: 'Jacket & Vest' },
  { value: GarmentType.TROUSER, label: 'Trouser' },
  { value: GarmentType.SHIRT, label: 'Shirt' },
  { value: GarmentType.SHERWANI, label: 'Sherwani' },
  { value: GarmentType.KURTA, label: 'Kurta' },
  { value: GarmentType.KAFTAN, label: 'Kaftans / Nigerian Set' },
  { value: GarmentType.ALTERATION, label: 'Alteration' }
]

export const GARMENT_DISPLAY_LABELS: Record<GarmentType, string> = {
  [GarmentType.JACKET_VEST]: 'Jacket & Vest',
  [GarmentType.TROUSER]: 'Trouser',
  [GarmentType.SHIRT]: 'Shirt',
  [GarmentType.SHERWANI]: 'Sherwani',
  [GarmentType.KURTA]: 'Kurta',
  [GarmentType.KAFTAN]: 'Kaftans / Nigerian Set',
  [GarmentType.ALTERATION]: 'Alteration'
}

export const GARMENT_SUB_GARMENT_OPTIONS: Record<GarmentType, GarmentSubGarmentOption[]> = {
  [GarmentType.JACKET_VEST]: [
    { value: 'Semi Bespoke', label: 'Semi Bespoke' },
    { value: 'Fully Bespoke', label: 'Fully Bespoke' },
    { value: 'Tail Coat Jacket', label: 'Tail Coat Jacket' },
    { value: 'Over Coat Jacket', label: 'Over Coat Jacket' },
    { value: 'Sahara Jacket', label: 'Sahara Jacket' },
    { value: 'Safari Jacket', label: 'Safari Jacket' },
    { value: 'Tuxedo Jacket', label: 'Tuxedo Jacket' },
    { value: 'Small Jacket', label: 'Small Jacket' },
    { value: 'Ladies Jacket', label: 'Ladies Jacket' },
    { value: 'Indowest Jacket', label: 'Indowest Jacket' },
    { value: 'Sherwani Jacket', label: 'Sherwani Jacket' }
  ],
  [GarmentType.TROUSER]: [
    { value: 'Trouser', label: 'Trouser' },
    { value: 'Jean Trouser', label: 'Jean Trouser' },
    { value: 'Coord Trouser', label: 'Coord Trouser' },
    { value: 'Nigerian Trouser', label: 'Nigerian Trouser' },
    { value: 'Short Trouser', label: 'Short Trouser' },
    { value: 'Ladies Trouser', label: 'Ladies Trouser' }
  ],
  [GarmentType.SHIRT]: [
    { value: 'Shirt', label: 'Shirt' },
    { value: 'Shirt Designer', label: 'Shirt Designer' },
    { value: 'Over Shirt', label: 'Over Shirt' },
    { value: 'T Shirt', label: 'T Shirt' }
  ],
  [GarmentType.SHERWANI]: [
    { value: 'Sherwani', label: 'Sherwani' }
  ],
  [GarmentType.KURTA]: [
    { value: 'Kurta', label: 'Kurta' },
    { value: 'Kurta Designer', label: 'Kurta Designer' }
  ],
  [GarmentType.KAFTAN]: [
    { value: 'Kaftan', label: 'Kaftan' },
    { value: 'Nigerian Top', label: 'Nigerian Top' },
    { value: 'Nigerian Big Top', label: 'Nigerian Big Top' }
  ],
  [GarmentType.ALTERATION]: []
}

export const DEFAULT_GARMENT_SUB_GARMENT: Record<Exclude<GarmentType, GarmentType.ALTERATION>, string> = {
  [GarmentType.JACKET_VEST]: 'Semi Bespoke',
  [GarmentType.TROUSER]: 'Trouser',
  [GarmentType.SHIRT]: 'Shirt',
  [GarmentType.SHERWANI]: 'Sherwani',
  [GarmentType.KURTA]: 'Kurta',
  [GarmentType.KAFTAN]: 'Kaftan'
}

export const MEASUREMENT_REQUIRED_GARMENT_TYPES = new Set<GarmentType>([
  GarmentType.JACKET_VEST,
  GarmentType.TROUSER,
  GarmentType.SHIRT,
  GarmentType.SHERWANI,
  GarmentType.KURTA,
  GarmentType.KAFTAN
])

export const FABRIC_REQUIRED_GARMENT_TYPES = new Set<GarmentType>([
  GarmentType.JACKET_VEST,
  GarmentType.TROUSER,
  GarmentType.SHIRT,
  GarmentType.SHERWANI,
  GarmentType.KURTA,
  GarmentType.KAFTAN
])

export const ALTERATION_GARMENT_TYPE = GarmentType.ALTERATION

export const getGarmentDisplayLabel = (garmentType: GarmentType): string => GARMENT_DISPLAY_LABELS[garmentType]

export const getSubGarmentOptions = (garmentType: GarmentType): GarmentSubGarmentOption[] =>
  GARMENT_SUB_GARMENT_OPTIONS[garmentType] || []

export const getDefaultSubGarmentType = (garmentType: GarmentType): string | undefined => {
  if (garmentType === GarmentType.ALTERATION) {
    return undefined
  }

  return DEFAULT_GARMENT_SUB_GARMENT[garmentType]
}

export const isMeasurementRequiredForGarmentType = (garmentType: GarmentType): boolean =>
  MEASUREMENT_REQUIRED_GARMENT_TYPES.has(garmentType)

export const isFabricRequiredForGarmentType = (garmentType: GarmentType): boolean =>
  FABRIC_REQUIRED_GARMENT_TYPES.has(garmentType)

export const isAlterationGarmentType = (garmentType: GarmentType): boolean =>
  garmentType === GarmentType.ALTERATION
